import { SelectableBillingPeriodTypes } from '@constants/billingPeriods'
import {
  CREDIT_CARD,
  INVOICE,
  PAYPAL_CHECKOUT,
  PRZELEWY24
} from '@constants/paymentMethods'
import { useUserContext } from '@contexts/user'
import NiceModal from '@ebay/nice-modal-react'
import { getErrorMessage } from '@helpers/errorMessages'
import { useLazyService } from '@hooks/useLazyService'
import { emitTimingToastToggle } from '@landingi/landingi-ui-kit'
import { verifyWith3DS } from '@pages/Authentication/helpers/braintree'
import { PAYMENTS } from '@routes/path'
import { CheckoutRoutesTypes } from '@routes/path/payments'
import { getPaymentsProvider } from '@services/checkout/getPaymentsProvider'
import { payByPrzelewy24 } from '@services/checkout/payByPrzelewy24'
import { requestInvoice } from '@services/checkout/requestInvoice'
import {
  createBraintreeNonce,
  createBraintreeToken,
  getPayments,
  postPaymentMethods,
  postSubscriptions,
  postSubscriptionsPaypal
} from '@services/payments'
import { emitTimingToast } from '@ui-kit'
import axios, { AxiosError } from 'axios'
import { client, paypalCheckout } from 'braintree-web'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BUTTONS_DATA_TEST_IDS, ButtonsDataTestIdsTypes } from '../constants'
import { hasValidCreditCardResponse } from '../helpers/hasValidCreditCardResponse'
import { isVaultFlowType } from '../helpers/isVaultFlowType'
import { MODAL_PAYPAL } from '../modals/ModalPayPal'

interface GetPaymentActionProps {
  goToStep: (stepRoute: CheckoutRoutesTypes) => void
  chosenBillingPeriod: SelectableBillingPeriodTypes
  changeStep: (step: CheckoutRoutesTypes) => void
}

export const useGetPaymentAction = ({
  goToStep,
  chosenBillingPeriod,
  changeStep
}: GetPaymentActionProps) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const [csrfRetryAttempted, setCsrfRetryAttempted] = useState(0)

  const { csrfToken, mutateCSRFToken, csrfTokenError } = useUserContext()

  const handleOnError = async (
    error: AxiosError,
    dataTestId: ButtonsDataTestIdsTypes
  ) => {
    const { response } = error

    if (csrfRetryAttempted < 3 && response?.status === 400 && !csrfTokenError) {
      setCsrfRetryAttempted(csrfRetryAttempted + 1)

      // fetch a new CSRF token to resolve the "Invalid CSRF token" issue
      await mutateCSRFToken()

      // automatically re-submit the form after the CSRF token has been refreshed
      document
        .querySelector<HTMLButtonElement>(`[data-testid="${dataTestId}"]`)
        ?.click()

      return
    }

    emitTimingToast({
      message: t(getErrorMessage()),
      type: 'error'
    })
  }

  const handleOnSuccess = async () => {
    const data = await getPaymentsProvider(csrfToken)

    setCsrfRetryAttempted(0)

    changeStep(PAYMENTS.CHECKOUT.CONFIRMATION)

    switch (data.name) {
      case INVOICE:
        navigate(PAYMENTS.SUBSCRIPTION.SUBSCRIPTION)
        break
      case PRZELEWY24:
        if (data.url) {
          window.location.href = data.url
        } else {
          emitTimingToastToggle(t('error.page.generic.title'), 'alert')
        }
        break
      default:
        emitTimingToastToggle(t('error.page.generic.title'), 'alert')
        break
    }
  }

  const [createBraintreeTokenRequest] = useLazyService(createBraintreeToken, {
    forwardError: true
  })

  const [getPaymentsRequest] = useLazyService(getPayments, {
    forwardError: true
  })

  const handlePayByCreditCard = async ({
    cardNumber,
    cardExpiry,
    cardCvv
  }: {
    cardNumber: string
    cardExpiry: string
    cardCvv: string
  }) => {
    const paymentsInfo = await getPaymentsRequest()
    const { email } = paymentsInfo.account
    const { identifier: currentPackageIdentifier } = paymentsInfo.package

    const braintreeToken = await createBraintreeTokenRequest()

    const clientInstance = await client.create({
      authorization: braintreeToken.token
    })

    // @ts-ignore
    const creditCardResponse = await clientInstance.request({
      endpoint: 'payment_methods/credit_cards',
      method: 'post',
      data: {
        creditCard: {
          number: cardNumber,
          expirationDate: cardExpiry,
          cvv: cardCvv,
          options: {
            validate: true
          }
        }
      }
    })

    if (!hasValidCreditCardResponse(creditCardResponse)) {
      emitTimingToastToggle(
        t('registration.flow.credit.card.step.wrong.credit.card.data'),
        'alert'
      )
      return
    }

    const { nonce, binData } = creditCardResponse.creditCards[0]

    const paymentMethodToken = (await postPaymentMethods(nonce)).data

    const testChargeAmount = '1.00'

    const braintreeNonce = await createBraintreeNonce(paymentMethodToken.token)

    const paymentMethodNonce = braintreeNonce.data

    const { enrichedNonce, shouldPostSubscription } = await verifyWith3DS(
      testChargeAmount,
      paymentMethodNonce.nonce,
      binData,
      email,
      clientInstance
    )

    if (shouldPostSubscription) {
      await postSubscriptions(
        enrichedNonce,
        chosenBillingPeriod,
        currentPackageIdentifier
      )

      goToStep(PAYMENTS.CHECKOUT.CONFIRMATION)
    } else {
      emitTimingToastToggle(
        t('registration.flow.credit.card.step.wrong.credit.card.data'),
        'alert'
      )
    }
  }

  const handlePayByPayPal = async () => {
    const paymentsInfo = await getPaymentsRequest()
    const { identifier: currentPackageIdentifier } = paymentsInfo.package

    const braintreeToken = await createBraintreeTokenRequest()

    const clientInstance = await client.create({
      authorization: braintreeToken.token
    })

    const paypalCheckoutInstance = await paypalCheckout.create({
      client: clientInstance
    })

    await paypalCheckoutInstance.loadPayPalSDK({
      vault: true
    })

    const { paypal } = window

    if (!paypal) {
      emitTimingToastToggle(t('paypal.error.sdk'), 'alert')
      return
    }

    try {
      const flow: unknown = 'vault'

      if (!isVaultFlowType(flow)) {
        emitTimingToastToggle(t('paypal.error.message'), 'alert')
        return
      }

      const button = paypal.Buttons({
        style: {
          height: 44
        },

        fundingSource: 'paypal',

        createBillingAgreement: async () =>
          paypalCheckoutInstance.createPayment({
            flow
          }),

        onApprove: async data => {
          const payload = await paypalCheckoutInstance.tokenizePayment(data)

          const { nonce } = payload

          await postPaymentMethods(nonce)

          await postSubscriptionsPaypal(
            chosenBillingPeriod,
            currentPackageIdentifier
          )

          NiceModal.remove(MODAL_PAYPAL)

          goToStep(PAYMENTS.CHECKOUT.CONFIRMATION)

          return payload
        },

        onError: () => {
          emitTimingToastToggle(t('paypal.error.message'), 'alert')
        },

        onCancel: () => {
          emitTimingToastToggle(t('paypal.cancel.message'), 'alert')
        }
      })

      NiceModal.show(MODAL_PAYPAL, { button })
    } catch (error) {
      emitTimingToastToggle(t('paypal.error.message'), 'alert')
    }
  }

  const handlePayByPrzelewy24 = async () => {
    try {
      await payByPrzelewy24({
        billingPeriod: chosenBillingPeriod,
        csrfToken
      })

      await handleOnSuccess()
    } catch (error) {
      await handleOnError(error as AxiosError, BUTTONS_DATA_TEST_IDS.PRZELEWY24)
    }
  }

  const handleRequestInvoice = async () => {
    try {
      await requestInvoice({
        billingPeriod: chosenBillingPeriod,
        csrfToken
      })

      await handleOnSuccess()
    } catch (error) {
      if (axios.isAxiosError(error)) {
        await handleOnError(error, BUTTONS_DATA_TEST_IDS.INVOICE)
      } else {
        emitTimingToast({
          message: t('error.message.default'),
          type: 'error'
        })
      }
    }
  }

  type CreditCardInfo = {
    cardNumber: string
    cardExpiry: string
    cardCvv: string
  }

  type PaymentActionType = {
    [CREDIT_CARD]: (creditCardInfo: CreditCardInfo) => Promise<void>
    [PAYPAL_CHECKOUT]: () => Promise<void>
    [PRZELEWY24]: () => Promise<void>
    [INVOICE]: () => Promise<void>
  }

  const paymentAction: PaymentActionType = {
    [CREDIT_CARD]: creditCardInfo =>
      handlePayByCreditCard({ ...creditCardInfo }),
    [PAYPAL_CHECKOUT]: () => handlePayByPayPal(),
    [PRZELEWY24]: () => handlePayByPrzelewy24(),
    [INVOICE]: () => handleRequestInvoice()
  }

  return paymentAction
}
